/**
 * ReportsIndexPage — `/reports`.
 *
 * Lists the 10 pre-built reports as tiles, grouped by category, linking to
 * each report's surface under `/reports/<slug>`.
 */
import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { FileBarChart } from 'lucide-react';

import { PREBUILT_REPORTS } from '../../lib/api/reports';
import { PageHeader } from '../shared/StaffUI';

export function ReportsIndexPage(): JSX.Element {
  // Group by category, preserve original order within each.
  const byCategory = new Map<string, typeof PREBUILT_REPORTS[number][]>();
  for (const r of PREBUILT_REPORTS) {
    const list = byCategory.get(r.category) ?? [];
    list.push(r);
    byCategory.set(r.category, list);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        description="Pre-built reports across operations, finance, compliance, and franchise."
      />

      {[...byCategory.entries()].map(([category, items]) => (
        <section key={category}>
          <h3 className="font-semibold text-gray-900 mb-3">{category}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((r) => (
              <Link
                key={r.slug}
                to={`/reports/${r.slug}`}
                className="block bg-white border border-border rounded-xl p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <FileBarChart className="w-5 h-5 text-nipost-green flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">{r.title}</h4>
                    <p className="text-xs text-muted mt-1 line-clamp-3">{r.description}</p>
                    <p className="text-[10px] font-mono text-muted mt-2">{r.reportKey}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
